import { useEffect, useState } from "react";
import style from "@/styles/pages/Profile/PhoneVerification.module.css";
import { RootState } from "@/store/dtm";
import { useSelector } from "react-redux";
import InputMask from "react-input-mask";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setPhoneNumber } from "@/slices/applicantSlice";
import { createApiClient } from "@/lib/Requests";
import { CreateTwilioCode } from "@/lib/DTMWebAPIClient.generated";
import SlideTransition from "@/styles/animation/SlideTransition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function PhoneVerification() {

  const client = createApiClient({ timeout: 60000 });

  function sendCode(phoneNumber: string, channel: string) {
    if (phoneNumber === null || phoneNumber === "") {
      methods.setError("phoneNumber",
        {
          type: "custom",
          message: "Please enter phone number"
        })
    } else {
      if (channel == "sms") setIsSendingSMSRequest(true);
      if (channel == "voice") setIsSendingPhoneRequest(true);
      client.apiAuthGenerateverificationcode(new CreateTwilioCode({
        verificationType: channel,
        phoneNumber: phoneNumber
      })).then(function (codeSent) {
        setIsSendingSMSRequest(false);
        setIsSendingPhoneRequest(false);
        if (codeSent) {
          navigate("/single/verification-code", {replace: true});
        }
        else {
          setSendError(true);
        }
      }).catch((error) => {
        setIsSendingSMSRequest(false);
        setIsSendingPhoneRequest(false);
        setSendError(true);
      });

    }
  }
  const navigate = useNavigate();

  const [sendError, setSendError] = useState(false);
  const [isSendingSMSRequest, setIsSendingSMSRequest] = useState(false);
  const [isSendingPhoneRequest, setIsSendingPhoneRequest] = useState(false);

  const dispatch = useDispatch();
  const phoneNumber = useSelector(
    (state: RootState) => state.applicant.applicant.phoneNumber
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);

  const methods = useForm({
    defaultValues: {
      phoneNumber: applicant.phoneNumber,
    },
  });


  useEffect(() => {
    // TypeScript users 
    const subscription = methods.watch(() => methods.handleSubmit(onSubmit)())
    return () => subscription.unsubscribe();
  }, [methods.handleSubmit, methods.watch]);


  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
  }
  //where they are setting the phone number
  const onSubmit = (data: any) => {
    dispatch(setPhoneNumber(data.phoneNumber))
  };

  useEffect(() => {
    document.title = "We need to verify your phone number";
    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, 0, applicant);

  }, []);

  useEffect(() => {
    if (phoneNumber == undefined || phoneNumber.trim() == '') {
      return;
    }
    methods.reset({ phoneNumber: phoneNumber })
  }, [phoneNumber]);

  return (
    <SlideTransition>
      <main aria-labelledby="main-content" className="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20" id="main">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
              <div className={style.psHeading}>
                <div>
                  <h1>
                    We need to verify your phone number
                  </h1>
                  <p className="phone-verify-text">
                    Which number should we send your verification code to? If you're using a landline, please select 'Verify by voice call'.
                  </p>

                  <div className="flex md:flex-row flex-col w-max">
                    <div className="mt-5">
                      <label htmlFor="phoneNumber">Phone number</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.phoneNumber
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <InputMask
                          type="tel"
                          placeholder="(000) 000-0000"
                          alwaysShowMask={false}
                          maskChar={null}
                          mask="(999) 999-9999"
                          className={clsx(
                            methods.formState?.errors?.phoneNumber?.message
                              ? "error"
                              : "",
                            "mt-2 w:[200px] md:w-[305px]"
                          )}
                          {...methods.register("phoneNumber", {
                            required: "Enter a valid phone number",
                            pattern: {
                              value:
                                // eslint-disable-next-line no-useless-escape
                                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                              message: "Enter a valid phone number",
                            },
                          })}
                        />
                      </div>
                      {methods.formState?.errors?.phoneNumber?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          Enter a valid phone number
                        </span>
                      )}
                    </div>
                  </div>


                  <h1 className={style.psListHeadingLight}>To verify this number, we will send you a six-digit code by text message or automated voice call. </h1>
                  <div className="">
                    <div className="flex max-sm:hidden">
                      <div className="mb-5">
                        <button
                          onClick={() => sendCode(phoneNumber, "sms")}
                          className="wide mt-5 w-169 sm:w-28 h-11 px-6"
                          disabled={isSendingSMSRequest || isSendingPhoneRequest}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSendingSMSRequest ? "" : "hidden")} />
                          Verify by SMS
                        </button>
                      </div>
                      <div className="mb-5">
                        <button
                          onClick={() => sendCode(phoneNumber, "call")}
                          className="wide mt-5 w-169 sm:w-28 h-11 px-6 ml-5"
                          disabled={isSendingSMSRequest || isSendingPhoneRequest}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSendingPhoneRequest ? "" : "hidden")} />
                          Verify by Voice Call
                        </button>
                      </div>
                    </div>
                    <div className="sm:hidden">
                      <div className="mb-5">
                        <button
                          onClick={() => sendCode(phoneNumber, "sms")}
                          className="primary mt-5 w-full h-11 px-6"
                          disabled={isSendingSMSRequest || isSendingPhoneRequest}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSendingSMSRequest ? "" : "hidden")} />
                          Verify by SMS
                        </button>
                      </div>
                    </div>
                    <div className="sm:hidden">
                      <div className="mb-5">
                        <button
                          onClick={() => sendCode(phoneNumber, "call")}
                          className="primary mt-5 w-full h-11 px-6"
                          disabled={isSendingSMSRequest || isSendingPhoneRequest}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSendingPhoneRequest ? "" : "hidden")} />
                          Verify by Voice Call
                        </button>
                      </div>
                    </div>

                  </div>

                  {/* {methods.formState?.errors?.phoneNumber?.message ? (
                  <span className="flex items-center font-medium tracking-wide error-text text-xs mt-1 ml-1">
                    {methods.formState?.errors?.phoneNumber?.message}
                  </span>) : (null)} */}
                  {sendError && (
                    <div>
                      <span className="flex items-center font-medium tracking-wide error-text text-xs mt-1 ml-1">
                        Something went wrong when sending the verification code
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </main>
    </SlideTransition>
  );
}
